import React, {
  useState,
  useRef,
  useCallback,
  ReactNode,
  useEffect,
  useLayoutEffect,
} from "react";
import { Button } from "../Button";

interface SnapCarouselProps {
  children: ReactNode;
  className?: string;
  itemWidth?: string;
  gap?: string;
  startIndex?: number;
  infinite?: boolean;
  onSlideChange?: (index: number) => void;
  dotsVariant?: "default" | "centeredCounter";
  showAdjacent?: boolean;
  centerMode?: boolean;
}

export function SnapCarousel({
  children,
  className = "",
  itemWidth = "70vw",
  gap = "gap-2",
  startIndex = 0,
  infinite = false,
  onSlideChange,
  dotsVariant = "default",
  showAdjacent = true,
  centerMode = false,
}: SnapCarouselProps) {
  const rawItems = React.Children.toArray(children);
  const count = rawItems.length;

  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= 1024 : false,
  );

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const effectiveInfinite = isDesktop ? infinite && count > 3 : infinite;

  const viewportRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const prevPadCountRef = useRef<number>(1);
  const [padCount, setPadCount] = useState<number>(1);
  const [layout, setLayout] = useState({
    viewportWidth: 0,
    slideWidthPx: 0,
    stepPx: 0,
  });

  const items = React.useMemo(() => {
    if (!effectiveInfinite) return rawItems;
    if (count <= 1) return rawItems;

    const safePad = Math.min(padCount, count);
    const leftClones = rawItems.slice(Math.max(0, count - safePad));
    const rightClones = rawItems.slice(0, safePad);

    return [...leftClones, ...rawItems, ...rightClones];
  }, [effectiveInfinite, rawItems, count, padCount]);

  const [current, setCurrent] = useState(startIndex);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);

  const startXRef = useRef(0);
  const allowTransitionRef = useRef(true);

  const centerModeEnabled = centerMode || (showAdjacent && count > 3);
  const effectiveItemWidth = showAdjacent ? itemWidth : "100%";

  const effectivePadCount =
    effectiveInfinite && count > 1 ? Math.min(padCount, count) : 0;

  const realIndex = effectiveInfinite
    ? (current - effectivePadCount + count) % count
    : current;

  const measureLayout = useCallback(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track) return;

    const children = Array.from(track.children) as HTMLElement[];
    if (children.length === 0) return;

    const slideWidthPx = children[0].offsetWidth;
    let stepPx = slideWidthPx;

    if (children.length >= 2) {
      const firstLeft = children[0].offsetLeft;
      const secondLeft = children[1].offsetLeft;
      const delta = secondLeft - firstLeft;
      if (delta > 0) stepPx = delta;
    }

    setLayout((prev) => {
      if (
        prev.viewportWidth === viewport.clientWidth &&
        prev.slideWidthPx === slideWidthPx &&
        prev.stepPx === stepPx
      ) {
        return prev;
      }
      return {
        viewportWidth: viewport.clientWidth,
        slideWidthPx,
        stepPx,
      };
    });
    if (effectiveInfinite && !isDragging && count > 1 && slideWidthPx > 0) {
      const visible = Math.ceil(viewport.clientWidth / slideWidthPx);
      const nextPad = Math.max(1, visible + 1);
      const capped = Math.min(nextPad, count);

      setPadCount((prev) => (prev === capped ? prev : capped));
    }
  }, [effectiveInfinite, isDragging, count]);

  useEffect(() => {
    if (!effectiveInfinite || count <= 1) return;
    const oldPad = prevPadCountRef.current;
    if (oldPad === padCount) return;

    const nextReal = (current - oldPad + count) % count;
    setCurrent(Math.min(padCount, count) + nextReal);
    prevPadCountRef.current = padCount;
  }, [padCount, effectiveInfinite, count, current]);

  useLayoutEffect(() => {
    if (count === 0) return;
    if (!effectiveInfinite || count <= 1) {
      setCurrent((c) => Math.max(0, Math.min(count - 1, c)));
      return;
    }
    const pad = Math.min(padCount, count);
    const minI = pad;
    const maxI = pad + count - 1;
    setCurrent((c) => {
      if (c >= minI && c <= maxI) return c;
      return pad + (((startIndex % count) + count) % count);
    });
  }, [effectiveInfinite, count, startIndex, padCount]);

  useLayoutEffect(() => {
    measureLayout();
  }, [
    measureLayout,
    effectiveItemWidth,
    gap,
    effectiveInfinite,
    count,
    centerModeEnabled,
  ]);

  useEffect(() => {
    measureLayout();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measureLayout);
      return () => window.removeEventListener("resize", measureLayout);
    }

    const viewport = viewportRef.current;
    if (!viewport) return;

    const ro = new ResizeObserver(() => measureLayout());
    ro.observe(viewport);
    window.addEventListener("resize", measureLayout);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measureLayout);
    };
  }, [measureLayout]);

  const handleStart = useCallback((clientX: number) => {
    setIsDragging(true);
    startXRef.current = clientX;
  }, []);

  const handleMove = useCallback(
    (clientX: number) => {
      if (!isDragging) return;
      setDragOffset(clientX - startXRef.current);
    },
    [isDragging],
  );

  const handleEnd = useCallback(() => {
    if (!isDragging) return;

    setIsDragging(false);

    const threshold = 50;
    let next = current;

    if (dragOffset < -threshold) next += 1;
    if (dragOffset > threshold) next -= 1;

    if (!effectiveInfinite) {
      next = Math.max(0, Math.min(count - 1, next));
    }

    setCurrent(next);
    setDragOffset(0);
  }, [isDragging, dragOffset, current, effectiveInfinite, count]);

  useEffect(() => {
    if (!effectiveInfinite) {
      onSlideChange?.(current);
      return;
    }

    if (count <= 1) {
      onSlideChange?.(0);
      allowTransitionRef.current = true;
      return;
    }

    const leftBound = effectivePadCount;
    const rightBound = effectivePadCount + count;
    if (current >= leftBound && current < rightBound) {
      onSlideChange?.(realIndex);
      allowTransitionRef.current = true;
      return;
    }

    const timeout = setTimeout(() => {
      allowTransitionRef.current = false;

      requestAnimationFrame(() => {
        const snapped =
          current >= rightBound ? current - count : current + count;
        setCurrent(snapped);

        requestAnimationFrame(() => {
          allowTransitionRef.current = true;
        });
      });
    }, 300);

    return () => clearTimeout(timeout);
  }, [
    current,
    effectiveInfinite,
    count,
    realIndex,
    onSlideChange,
    padCount,
    effectivePadCount,
  ]);

  if (count === 0) return null;

  const translateX =
    layout.stepPx > 0
      ? centerModeEnabled
        ? (layout.viewportWidth - layout.slideWidthPx) / 2 -
        current * layout.stepPx +
        dragOffset
        : -current * layout.stepPx + dragOffset
      : 0;

  return (
    <div ref={viewportRef} className={`overflow-hidden ${className}`}>
      <div
        onMouseDown={(e) => handleStart(e.clientX)}
        onMouseMove={(e) => handleMove(e.clientX)}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
        onTouchStart={(e) => handleStart(e.touches[0].clientX)}
        onTouchMove={(e) => handleMove(e.touches[0].clientX)}
        onTouchEnd={handleEnd}
      >
        <div
          ref={trackRef}
          className={`flex ${gap} ${!isDragging && allowTransitionRef.current
            ? "transition-transform duration-300 ease-out rounded-lg"
            : ""
            }`}
          style={{ transform: `translateX(${translateX}px)` }}
        >
          {items.map((child, i) => (
            <div
              key={i}
              className="shrink-0"
              style={{ width: effectiveItemWidth, cursor: "grab" }}
            >
              {child}
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-center gap-2 mt-4">
        {rawItems.map((_, i) => {
          if (dotsVariant === "centeredCounter") {
            const total = rawItems.length;
            const prev = (realIndex - 1 + total) % total;
            const next = (realIndex + 1) % total;

            const isVisible = i === prev || i === realIndex || i === next;
            if (!isVisible) return null;

            const isActive = i === realIndex;

            return (
              <Button
                key={i}
                onClick={() =>
                  setCurrent(effectiveInfinite ? effectivePadCount + i : i)
                }
                aria-label={`Go to slide ${i + 1}`}
                className={
                  isActive
                    ? "min-w-[30px] py-1 px-2 rounded-full bg-white text-black text-[11px] font-semibold leading-none flex items-center justify-center"
                    : "mt-1 w-2.5 h-2.5 rounded-full bg-[#585858]"
                }
              >
                {isActive ? `${realIndex + 1}/${total}` : ""}
              </Button>
            );
          }

          return (
            <Button
              key={i}
              onClick={() =>
                setCurrent(effectiveInfinite ? effectivePadCount + i : i)
              }
              className={`h-2 w-2 rounded-full ${i === realIndex ? "bg-white" : "bg-gray-400"
                }`}
              aria-label={`Go to slide ${i + 1}`}
            />
          );
        })}
      </div>
    </div>
  );
}